import h from 'hyperscript';

import { assert, assertType } from '@disy/lib/custom-error';

import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { CONDITION_INTERVAL_VALUE_TYPE } from 'cadenza/workbook/conditions/condition-const';
import type { ConditionDetailsCreator } from 'cadenza/workbook/conditions/workbook-read-only-condition';
import type {
  ConditionDto,
  DefaultConditionValueItemDto,
  WorkbookConditionValuesDto,
  WorkbookSpatialConditionValueItemDto,
  WorkbookValueIntervalConditionValueItemDto,
} from 'cadenza/workbook/model/condition-dto';
import { ConditionSettingsKey } from 'cadenza/workbook/model/condition-settings-key.gen';
import { getQlConditionValue, getQlConditionVariables } from 'cadenza/workbook/ql-condition/ql-condition-utils';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';

import './create-condition.css';

export function conditionHasValues(condition: ConditionDto): boolean {
  if (condition.type === 'PolygonCondition') {
    return getPolygonConditionValues(condition).length > 0;
  }
  return getConditionValueItems(condition).length > 0;
}

export function isSpatialConditionWithValueItems(condition: ConditionDto): boolean {
  if (condition.type === 'PolygonCondition') {
    return getConditionValueItems(condition).length > 0;
  }
  return false;
}

function getPolygonConditionValues(condition: ConditionDto) {
  const firstItem = condition.values?.valueItems?.[0] as WorkbookSpatialConditionValueItemDto | undefined;
  return firstItem?.values ?? [];
}

function getConditionValueItems(condition: ConditionDto) {
  return condition.values?.valueItems ?? [];
}

export function supportsReset(condition: ConditionDto) {
  // QL condition without variables can't be reset
  return condition.type !== 'QlCondition' || getQlConditionVariables(condition).length > 0;
}

export function isMandatory(condition: ConditionDto) {
  return getSetting<boolean>(condition, ConditionSettingsKey.toPersistenceKey('CONDITION_MANDATORY'), false);
}

export function getDeactivated(condition: ConditionDto) {
  return isDeactivatedForAllWorksheets(condition) || (isDeactivatedForAnyWorksheet(condition) && 'INDETERMINATE');
}

function isDeactivatedForAllWorksheets(condition: ConditionDto) {
  return getSetting<boolean>(condition, ConditionSettingsKey.toPersistenceKey('CONDITION_DEACTIVATED'), false);
}

export function isDeactivatedForWorksheet(condition: ConditionDto, activeWorksheetId: WorksheetId) {
  const deactivated = getDeactivated(condition);
  if (deactivated === false || deactivated === true) {
    return deactivated;
  }
  return getDeactivatedForWorksheets(condition).includes(activeWorksheetId);
}

export function isDeactivatedForAnyWorksheet(condition: ConditionDto) {
  return getDeactivatedForWorksheets(condition).length > 0;
}

export function getDeactivatedForWorksheets(condition: ConditionDto) {
  return getSetting<WorksheetId[]>(
    condition,
    ConditionSettingsKey.toPersistenceKey('CONDITION_DEACTIVATED_FOR_WORKSHEETS'),
    [],
  );
}

export function getHidden(condition: ConditionDto) {
  return isHiddenForAllWorksheets(condition) || (isHiddenForAnyWorksheet(condition) && 'INDETERMINATE');
}

export function isHidden(condition: ConditionDto, activeWorksheetId: WorksheetId) {
  return isHiddenForAllWorksheets(condition) || isHiddenForWorksheet(condition, activeWorksheetId);
}

function isHiddenForAllWorksheets(condition: ConditionDto) {
  return getSetting<boolean>(condition, ConditionSettingsKey.toPersistenceKey('CONDITION_HIDDEN'), false);
}

export function isHiddenForAnyWorksheet(condition: ConditionDto) {
  return (
    getSetting<boolean>(condition, ConditionSettingsKey.toPersistenceKey('CONDITION_HIDDEN'), false) ||
    getSetting<WorksheetId[]>(condition, ConditionSettingsKey.toPersistenceKey('CONDITION_HIDDEN_FOR_WORKSHEETS'), [])
      .length > 0
  );
}

function isHiddenForWorksheet(condition: ConditionDto, activeWorksheetId: WorksheetId) {
  return getSetting<WorksheetId[]>(
    condition,
    ConditionSettingsKey.toPersistenceKey('CONDITION_HIDDEN_FOR_WORKSHEETS'),
    [],
  ).includes(activeWorksheetId);
}

export function areAllHidden(conditions: ConditionDto[], activeWorksheetId: WorksheetId) {
  return conditions.every((condition) => isHidden(condition, activeWorksheetId));
}

// As we have invented at least 4 different ways to express "no condition value set", deal with the consequences here.
export function hasValuesSet(condition: ConditionDto) {
  if (condition.type === 'PolygonCondition') {
    // This is caused by the way a spatial condition stores its individual geometries.
    // After QA we might have a better insight into which other conditions also require hacks.
    // We then can consider the big picture and how to properly address these inconsistencies.
    return ((condition.values?.valueItems[0] as WorkbookSpatialConditionValueItemDto)?.values.length ?? 0) > 0;
  }
  if (condition.type === 'QlCondition') {
    const qlConditionVariables = getQlConditionVariables(condition);
    if (qlConditionVariables.length) {
      return qlConditionVariables.some((variable) => variable.values.length > 0);
    } else {
      const expression = getQlConditionValue(condition)?.expression;
      return !!expression;
    }
  }
  if (condition.type === 'ValueIntervalCondition') {
    const valueIntervalCondition = condition.values?.valueItems[0] as WorkbookValueIntervalConditionValueItemDto;
    // Explicitly check against `undefined`, as `0` is a valid `start`/`end` value that would otherwise be turned into `false`.
    return valueIntervalCondition?.start !== undefined || valueIntervalCondition?.end !== undefined;
  }
  return (condition.values?.valueItems?.length ?? 0) > 0;
}

export function getSpatialConditionItemValues(condition: ConditionDto) {
  assert(condition.type === 'PolygonCondition', 'Not spatial condition!');
  // ?: condition can be without values
  return (
    condition.values?.valueItems.flatMap((conditionValueItem) => {
      assertType<WorkbookSpatialConditionValueItemDto>(
        conditionValueItem,
        conditionValueItem.type === 'spatialConditionDisplayValue',
        'Not spatial condition value!',
      );
      return conditionValueItem.values;
    }) ?? []
  );
}

export function formatConditionValueAsText(values?: WorkbookConditionValuesDto): string {
  if (!values) {
    return '';
  }

  switch (values.conditionType) {
    case 'QlCondition':
      return '[Cadenza Query Language condition]'; // TODO [CQL]: port printer to backend
    case 'PolygonCondition':
      return formatSpatialConditionValueAsText(values);
    default:
      return formatDefaultConditionValuesAsText(values);
  }
}

export function formatDefaultConditionValuesAsText(values: WorkbookConditionValuesDto) {
  return values.valueItems
    .map((value) => {
      if (value.type === CONDITION_INTERVAL_VALUE_TYPE) {
        const conditionIntervalValueItem = value as WorkbookValueIntervalConditionValueItemDto;
        return `${conditionIntervalValueItem.startFormatted} - ${conditionIntervalValueItem.endFormatted}`;
      }
      const defaultConditionValueItem = value as DefaultConditionValueItemDto;
      return defaultConditionValueItem.text;
    })
    .join(', ');
}

/**
 * Returns a table, date, interval or other "default" condition's formatted values.
 *
 * @param condition
 * @return Formatted text representation of the condition's value, including formatting for excluded values.
 */
export const formatDefaultConditionValues: ConditionDetailsCreator = (condition) => {
  if (!condition.values?.valueItems) {
    return;
  }

  const excludeValues =
    condition.conditionConfiguration && 'excludeValues' in condition.conditionConfiguration ?
      condition.conditionConfiguration.excludeValues
    : false;

  return condition.values.valueItems
    .map((value) => {
      const text =
        value.type === CONDITION_INTERVAL_VALUE_TYPE ?
          formatIntervalValueItem(value as WorkbookValueIntervalConditionValueItemDto) // We know because of the value type field
        : formatSimpleValueItem(value as DefaultConditionValueItemDto);
      const span = h('span.d-read-only-condition-value-item', text);
      if (excludeValues) {
        span.classList.add('is-excluded');
      }
      return span;
    })
    .flatMap((span, index) => {
      return (
          index === condition.values!.valueItems.length - 1 // we know condition.values is not undefined because of check earlier
        ) ?
          [span]
        : [span, ', '];
    });
};

function formatIntervalValueItem(value: WorkbookValueIntervalConditionValueItemDto) {
  return `${value.startFormatted} - ${value.endFormatted}`;
}

function formatSimpleValueItem(value: DefaultConditionValueItemDto) {
  if (value.type === 'tableCondition') {
    // when there is an attribute relation, we just want to show the first value
    return value.values[0];
  }
  return value.text;
}

export function formatSpatialConditionValueAsText(values: WorkbookConditionValuesDto) {
  return (
    (values.valueItems[0] as WorkbookSpatialConditionValueItemDto)?.values.map((it) => it.printName).join(',') ?? ''
  );
}
